/**
 * 「sugargirl 女孩」頁：在介面上驗收 AI 回覆品質。
 *
 * 版面刻意照房間／帳號那幾頁（`.cui-panel` 直排、一列一顆、動作在列尾），
 * 不新增一套卡片樣式。三塊由上到下：
 *   1. 代理不在／mock 的說明（只在需要時出現，附可行的解法，不是一句「失敗」）
 *   2. 已接上的女孩：模式開關、人設、最近 3 則 AI 草稿、退出
 *   3. 全部女孩（經 /api/sg-proxy）：未讀、展開看她的對話串、一鍵接上
 *
 * 所有打 API 的動作都走 `useSugargirl`，這一層只管顯示與本機互動（兩段式確認、輸入框）。
 */
import { useState } from 'react';
import { NoticeStack } from '@chat-rea/console-ui';
import type { AutoReplyMode, SgConversation, SgDraft } from './types';
import { MODE_HINT, MODE_LABEL, formatDraftTime } from './types';
import { draftsOf, type SugargirlState } from './useSugargirl';

export interface SugargirlPanelProps {
  sg: SugargirlState;
}

const CARD = 'rounded-[3px] border border-border bg-surface';

function ModeSwitch({
  mode,
  disabled,
  onChange,
}: {
  mode: AutoReplyMode;
  disabled: boolean;
  onChange: (mode: AutoReplyMode) => void;
}) {
  return (
    <div className="flex items-center gap-1" role="group" aria-label="AI 回覆模式">
      {(['draft', 'auto'] as const).map((value) => {
        const active = mode === value;
        return (
          <button
            key={value}
            type="button"
            className={`cui-btn cui-btn--sm${active ? ' cui-btn--primary' : ' cui-btn--ghost'}`}
            aria-pressed={active}
            disabled={disabled || active}
            onClick={() => onChange(value)}
            title={MODE_HINT[value]}
            data-testid={`sg-mode-${value}`}
          >
            {value === 'draft' ? '擬稿' : '自動送出'}
          </button>
        );
      })}
    </div>
  );
}

function DraftList({ drafts, loading, error }: { drafts: SgDraft[]; loading: boolean; error: string | null }) {
  if (loading) return <p className="cui-muted text-[11px]">讀取草稿中…</p>;
  if (error !== null) return <p className="cui-muted text-[11px]">草稿讀不到：{error}</p>;
  if (drafts.length === 0) {
    return (
      <p className="cui-muted text-[11px]">
        還沒有 AI 草稿。切到「擬稿」後，等客人再傳訊息（或請對方回一句），草稿就會出現在這裡。
      </p>
    );
  }
  return (
    <ul className="flex flex-col gap-1.5" data-testid="sg-drafts">
      {drafts.map((draft) => (
        <li key={draft.id} className="rounded-[2px] border border-border bg-background/60 px-2 py-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="cui-mono text-[10px] uppercase tracking-micro text-muted-foreground">AI 草稿</span>
            <span className="cui-mono text-[10px] cui-muted" title={new Date(draft.ts).toLocaleString('zh-TW')}>
              {formatDraftTime(draft.ts)}
            </span>
          </div>
          <p className="mt-0.5 whitespace-pre-wrap break-words text-[12px] leading-snug">{draft.text}</p>
        </li>
      ))}
    </ul>
  );
}

function ConversationRow({
  conversation,
  busy,
  onAttach,
}: {
  conversation: SgConversation;
  busy: boolean;
  onAttach: () => void;
}) {
  return (
    <li className="flex items-center justify-between gap-2 border-t border-border px-2 py-1.5">
      <div className="min-w-0">
        <p className="truncate text-[12px]">{conversation.customerName}</p>
        <p className="cui-mono truncate text-[10px] cui-muted">
          {conversation.id}
          {conversation.unreadCount > 0 ? ` · 未讀 ${conversation.unreadCount}` : ''}
          {conversation.blocked ? ' · 封鎖' : ''}
        </p>
      </div>
      <button
        type="button"
        className="cui-btn cui-btn--sm cui-btn--create"
        disabled={busy}
        onClick={onAttach}
        data-testid="sg-attach-conversation"
      >
        接上這串
      </button>
    </li>
  );
}

export function SugargirlPanel({ sg: state }: SugargirlPanelProps) {
  const [manualGirlId, setManualGirlId] = useState('');
  const [manualConversationId, setManualConversationId] = useState('');
  /** 兩段式退出：第一次點變「確定退出？」，第二次才真的刪。 */
  const [confirmDetach, setDetachArm] = useState<string | null>(null);

  const girlsById = new Map(state.girls.map((girl) => [girl.id, girl]));
  const busy = (key: string) => state.busy === key;

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2 p-3" data-testid="sg-page">
      <div className={`${CARD} cui-panel`}>
        <div className="cui-panel-head flex-wrap gap-2">
          <h2 className="cui-panel-title">sugargirl 女孩</h2>
          <span className="cui-muted text-[11px]">
            接上後看 AI 寫什麼，再決定要不要切成自動送出
          </span>
          <div className="ml-auto flex items-center gap-2">
            <button
              type="button"
              className="cui-btn cui-btn--sm cui-btn--ghost"
              onClick={() => void state.refresh()}
              disabled={state.sessionsLoading || state.girlsLoading}
              data-testid="sg-refresh"
            >
              {state.sessionsLoading || state.girlsLoading ? '讀取中…' : '重新整理'}
            </button>
          </div>
        </div>
        <div className="px-2 pb-2">
          <p className="cui-muted text-[11px]">
            <strong className="text-foreground">draft（擬稿）</strong>：{MODE_HINT.draft}
            <br />
            <strong className="text-foreground">auto（自動送出）</strong>：{MODE_HINT.auto}
          </p>
        </div>
      </div>

      <NoticeStack notices={state.notices} onDismiss={state.dismissNotice} top={44} />
      {state.notice !== null ? (
        <p className="rounded-[3px] border border-border bg-muted/40 px-2 py-1.5 text-[12px]">{state.notice}</p>
      ) : null}

      {!state.live ? (
        <div className="rounded-[3px] border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[12px]">
          目前是 mock 模式（沒有後端位址），這一頁沒有資料可看。請在 <code className="cui-mono">/config.json</code>{' '}
          設 <code className="cui-mono">apiBase</code> 後重新載入。
        </div>
      ) : null}

      {!state.proxyReady && state.live ? (
        <div className="rounded-[3px] border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[12px]" data-testid="sg-proxy-missing">
          <p className="font-medium">SG 代理不在：這一頁需要 Cloudflare Pages Functions</p>
          <p className="mt-0.5 cui-muted">
            「全部女孩」走同源的 <code className="cui-mono">/api/sg-proxy</code>，金鑰由伺服器端注入，
            瀏覽器不需要、也拿不到金鑰。會看到這段通常是用 <code className="cui-mono">vite dev</code>{' '}
            直接跑前端（沒有 Functions）：請改開正式站，或 <code className="cui-mono">npm run build</code> 後跑{' '}
            <code className="cui-mono">npx wrangler pages dev dist</code>。
          </p>
          <p className="mt-1 cui-muted">接上／退出／模式切換／草稿走一般登入，下面照樣可用。</p>
        </div>
      ) : null}

      {/* 已接上的女孩 ───────────────────────────────────────────── */}
      <div className={`${CARD} cui-panel min-h-0`}>
        <div className="cui-panel-head">
          <h2 className="cui-panel-title">已接上（{state.sessions.length}）</h2>
          <span className="cui-muted text-[11px]">session 開著才會收訊息；不驗收就退出，別放著跑</span>
        </div>

        <div className="flex flex-wrap items-end gap-2 border-t border-border px-2 py-2">
          <label className="flex flex-col gap-0.5 text-[11px]">
            <span className="cui-muted">女孩 ID（girlId）</span>
            <input
              className="cui-input h-7 w-[190px] text-[12px]"
              value={manualGirlId}
              onChange={(event) => setManualGirlId(event.target.value)}
              placeholder="例：g_1234"
              data-testid="sg-manual-girl"
            />
          </label>
          <label className="flex flex-col gap-0.5 text-[11px]">
            <span className="cui-muted">對話串 id（可留空＝最近有訊息那條）</span>
            <input
              className="cui-input h-7 w-[220px] text-[12px]"
              value={manualConversationId}
              onChange={(event) => setManualConversationId(event.target.value)}
              placeholder="選填"
              data-testid="sg-manual-conversation"
            />
          </label>
          <button
            type="button"
            className="cui-btn cui-btn--sm cui-btn--create"
            disabled={manualGirlId.trim() === '' || state.busy !== null}
            onClick={() => {
              void state.attach({
                girlId: manualGirlId.trim(),
                conversationId: manualConversationId.trim(),
                mode: 'draft',
              });
              setManualGirlId('');
              setManualConversationId('');
            }}
            data-testid="sg-attach-manual"
          >
            接上（預設擬稿）
          </button>
        </div>

        {state.sessionsError !== null && state.sessions.length > 0 ? (
          <p className="px-2 pb-2 text-[12px] text-red-600">讀取已接上的 session 失敗：{state.sessionsError}</p>
        ) : null}
        {/* 三態（#736）：載入中／讀不到／真的沒有，三者不能長一樣。 */}
        {state.sessions.length === 0 ? (
          state.sessionsLoading ? (
            <p
              className="cui-muted px-2 pb-2 text-[12px]"
              data-testid="sg-sessions-loading"
              data-state="loading"
              role="status"
              aria-busy="true"
            >
              讀取已接上的 session…
            </p>
          ) : state.sessionsError !== null ? (
            <div className="flex flex-col items-start gap-1 px-2 pb-3" data-testid="sg-sessions-error" data-state="error" role="alert">
              <p className="text-[12px] text-red-600">讀不到已接上的 session（不是「沒有接上」）：{state.sessionsError}</p>
              <button
                type="button"
                className="cui-btn cui-btn--sm"
                onClick={() => void state.refresh()}
                data-testid="sg-sessions-retry"
              >
                重試
              </button>
            </div>
          ) : (
            <p className="cui-muted px-2 pb-3 text-[12px]" data-testid="sg-sessions-empty" data-state="empty">
              還沒有接上的女孩。從上面「接上一串對話」開始。
            </p>
          )
        ) : null}

        <div className="flex flex-col">
          {state.sessions.map((session) => {
            const girl = girlsById.get(session.girlId);
            const info = state.status[session.id];
            const mode = state.status[session.id]?.mode ?? session.mode;
            const loadingDetail = state.detailLoading[session.id] === true;
            const detailError = state.detailError[session.id] ?? null;
            return (
              <div key={session.id} className="border-t border-border px-2 py-2" data-testid="sg-session-card">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-medium">
                      {girl?.nickname ?? session.nickname}
                      <span className="cui-mono ml-1 text-[10px] cui-muted">{session.girlId}</span>
                    </p>
                    <p className="cui-mono text-[10px] cui-muted">
                      session {session.id.slice(0, 8)} · {MODE_LABEL[mode]}
                      {info?.hasPersona === true ? ` · 人設 ${info.personaName ?? '（未命名）'}` : ' · 預設人設'}
                      {info !== undefined ? ` · 近一小時回覆 ${info.repliesLastHour}` : ''}
                    </p>
                  </div>
                  <div className="ml-auto flex items-center gap-2">
                    <ModeSwitch
                      mode={mode}
                      disabled={state.busy !== null}
                      onChange={(next) => void state.setMode(session.id, next)}
                    />
                    <button
                      type="button"
                      className="cui-btn cui-btn--sm cui-btn--ghost"
                      onClick={() => void state.reloadDetail(session.id)}
                      disabled={loadingDetail}
                      data-testid="sg-reload-drafts"
                    >
                      {loadingDetail ? '讀取中…' : '重讀'}
                    </button>
                    <button
                      type="button"
                      className={`cui-btn cui-btn--sm${confirmDetach === session.id ? ' cui-btn--armed' : ' cui-btn--danger'}`}
                      disabled={busy(`detach:${session.id}`)}
                      onClick={() => {
                        if (confirmDetach === session.id) {
                          setDetachArm(null);
                          void state.detach(session.id);
                        } else {
                          setDetachArm(session.id);
                        }
                      }}
                      onBlur={() => setDetachArm(null)}
                      data-testid="sg-detach"
                    >
                      {confirmDetach === session.id ? '確定退出？' : busy(`detach:${session.id}`) ? '退出中…' : '退出'}
                    </button>
                  </div>
                </div>
                <p className="cui-muted mt-1 text-[11px]">{MODE_HINT[mode]}</p>
                <div className="mt-1.5">
                  <DraftList drafts={draftsOf(state, session.id)} loading={loadingDetail} error={detailError} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* 全部女孩 ─────────────────────────────────────────────── */}
      <div className={`${CARD} cui-panel min-h-0 flex-1`}>
        <div className="cui-panel-head">
          <h2 className="cui-panel-title">全部女孩（{state.girls.length}）</h2>
          <span className="cui-muted text-[11px]">點一列展開她的對話串；接上會用「擬稿」模式開一條 session</span>
        </div>
        {state.girlsError !== null ? (
          <p className="px-2 pb-2 text-[12px] text-red-600">女孩清單讀取失敗：{state.girlsError}</p>
        ) : null}
        {state.girlsLoading ? <p className="cui-muted px-2 pb-2 text-[12px]">讀取中…</p> : null}
        {!state.girlsLoading && state.girls.length === 0 && state.girlsError === null ? (
          <p className="cui-muted px-2 pb-3 text-[12px]">後端回 0 位女孩。</p>
        ) : null}

        <div className="flex min-h-0 flex-col overflow-auto">
          {state.girls.map((girl) => {
            const expanded = state.expandedGirlId === girl.id;
            const attached = state.sessions.some((item) => item.girlId === girl.id);
            return (
              <div key={girl.id} className="border-t border-border" data-testid="sg-girl-row">
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-left hover:bg-muted/40"
                  onClick={() => void state.toggleGirl(girl.id)}
                  aria-expanded={expanded}
                  data-testid="sg-girl-toggle"
                >
                  <span className="cui-mono text-[10px] cui-muted">{expanded ? '▾' : '▸'}</span>
                  <span className="min-w-0 flex-1 truncate text-[12px]">{girl.nickname}</span>
                  <span className="cui-mono text-[10px] cui-muted">{girl.id}</span>
                  {girl.unreadCount > 0 ? <span className="cui-badge">{girl.unreadCount} 未讀</span> : null}
                  {attached ? <span className="cui-badge cui-badge--ut">已接上</span> : null}
                </button>

                {expanded ? (
                  <div className="bg-muted/20 pb-2">
                    <div className="flex items-center gap-2 px-2 py-1">
                      <span className="cui-muted text-[11px]">對話串</span>
                      <button
                        type="button"
                        className="cui-btn cui-btn--sm cui-btn--primary ml-auto"
                        disabled={state.busy !== null}
                        onClick={() =>
                          void state.attach({ girlId: girl.id, conversationId: '', mode: 'draft' })
                        }
                        data-testid="sg-attach-girl"
                      >
                        接上這位女孩（擬稿）
                      </button>
                    </div>
                    {state.conversationsLoading ? (
                      <p className="cui-muted px-2 text-[11px]">讀取對話串中…</p>
                    ) : null}
                    {state.conversationsError !== null ? (
                      <p className="px-2 text-[11px] text-red-600">{state.conversationsError}</p>
                    ) : null}
                    {!state.conversationsLoading && state.conversationsError === null ? (
                      state.conversations.length === 0 ? (
                        <p className="cui-muted px-2 text-[11px]">這位女孩目前沒有對話串。</p>
                      ) : (
                        <ul className="mx-2 overflow-hidden rounded-[2px] border border-border bg-surface">
                          {state.conversations.map((conversation) => (
                            <ConversationRow
                              key={conversation.id}
                              conversation={conversation}
                              busy={state.busy !== null}
                              onAttach={() =>
                                void state.attach({
                                  girlId: girl.id,
                                  conversationId: conversation.id,
                                  mode: 'draft',
                                })
                              }
                            />
                          ))}
                        </ul>
                      )
                    ) : null}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
